import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { ContentCard } from "components/ContentCard";
import { VirtualTransfer } from "components/VirtualTransfer";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import BasicTextInput from "components/Form/Inputs/common/BasicTextInput";
import CustomModal from "components/Modals/CustomModal";
import geographicalAPi from "services/apis/geographicalAPi";
import { CRUD_GEOGRAPHICAL_TYPES, getPathPrefix } from "utils/coreUtils";
import ToastUtils from "utils/ToastUtils";
import "../../styles/styles.scss";

const AddGeographicalArea = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const streamObj = useSelector((state) => state?.datastream?.selected);

  const [zipCodeData, setZipCodeData] = useState([]);
  const [geographicalState, setGeographicalState] = useState({
    areaName: "",
    isActive: false,
    other: "",
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [sheetNames, setSheetNames] = useState();
  const [selectedSheet, setSelectedSheet] = useState(0);

  const [targetKeys, setTargetKeys] = useState([]);
  const [zipCode, setZipCode] = useState("");
  const [showAddZipCode, setShowAddZipCode] = useState(false);
  const [showSheetSelectionModal, setShowSheetSelectionModal] = useState(false);

  const inputRef = useRef();
  const workbookRef = useRef(new ExcelJS.Workbook());
  const [searchParams] = useSearchParams();
  const type = searchParams.get("t");
  const params = useParams();
  const isViewOnly = type === CRUD_GEOGRAPHICAL_TYPES.VIEW;

  const breadCrumbTitle = useMemo(() => {
    if (type === CRUD_GEOGRAPHICAL_TYPES.CREATE) {
      return t("versionsForm.createTitle");
    }
    if (type === CRUD_GEOGRAPHICAL_TYPES.EDIT) {
      return t("versionsForm.updateTitle");
    }
    if (isViewOnly) {
      return t("versionsForm.viewTitle");
    }

    return t("versionsForm.createTitle");
  }, [searchParams]);

  useEffect(() => {
    if (type === CRUD_GEOGRAPHICAL_TYPES.EDIT || isViewOnly) {
      handleEdit();
    }
  }, [type]);

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const loadZipCode = (sheetIndex) => {
    const workbook = workbookRef.current;
    const worksheet = workbook.worksheets[sheetIndex];
    const rows = [];

    let zipKeyFound = false;

    worksheet.eachRow((row) => {
      const firstCell = row.getCell(1).value;
      const cellStringValue = firstCell.toString().toLowerCase();
      if (
        !zipKeyFound &&
        (cellStringValue === "zip" || cellStringValue === "zipcode")
      ) {
        zipKeyFound = true;
      } else {
        rows.push(firstCell);
      }
    });

    if (!zipKeyFound) {
      toast.error(t("geographical.form.noZipCodeFoundFromSheet"));
      return;
    }

    let zipCodeListObject = rows.map((item, i) => ({
      key: item,
      title: item,
      description: `description of zip${i + 1}`,
    }));
    setZipCodeData((prev) => [...prev, ...zipCodeListObject]);
    setShowSheetSelectionModal(false);
  };

  const handleDisplayFileDetails = () => {
    try {
      const files = inputRef.current.files;
      if (files.length) {
        const file = files[0];
        const reader = new FileReader();
        reader.onload = async (event) => {
          const workbook = workbookRef.current;
          // @ts-ignore
          await workbook.xlsx.load(event.target.result);
          const sheets = workbook.worksheets.map((sheet) => sheet.name);

          if (sheets.length === 1) {
            loadZipCode(0);
          } else {
            if (sheets.length > 1) {
              // @ts-ignore
              setSheetNames(sheets);
              setSelectedSheet(0);
              setShowSheetSelectionModal(true);
            }
          }
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      /* empty */
    }

    inputRef.current.value = "";
  };

  const handleEdit = async () => {
    // setIsLoading(true);
    try {
      const response = await geographicalAPi.getGeographicalAreaControlById({
        geographicalAreaControlId: params.geographicId,
      });
      geographicalState.areaName = response?.data?.geographicalAreaName;
      geographicalState.isActive = response?.data?.isActive;

      let takeTargetKey = response?.data?.zipCodeList.map((item) =>
        item.toString()
      );
      setTargetKeys(takeTargetKey);

      setZipCodeData(
        response?.data?.zipCodeList.map((item) => ({
          key: item?.toString(),
          title: item,
        }))
      );
      geographicalState.other = response?.data;
      setGeographicalState({ ...geographicalState });
    } catch (error) {
      console.error(error);
    }
    // setIsLoading(false);
  };

  const onTargetKeysChanged = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
  };

  //for error handling
  const buttonDisabled = useMemo(() => {
    if (
      Object.keys(errors).filter((e) => errors[e]).length > 0 ||
      !geographicalState.areaName
    ) {
      return true;
    } else {
      return false;
    }
  }, [errors, geographicalState]);

  const updateError = (key, value) => {
    setErrors((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const updateErrorForInputs = (inputValue, inputKey, error) => {
    if (!inputValue) {
      updateError(inputKey, error);
    } else {
      updateError(inputKey, undefined);
    }
  };
  const handleChange = (event) => {
    const inputValue = event.target.value;
    if (inputValue.length > 6) {
      setError("You cannot add more than 6 digits.");
    } else {
      setZipCode(inputValue);
      setError("");
    }
  };

  const onSubmitHandler = async () => {
    const createRequest = {
      geographicalAreaControlId:
        type === CRUD_GEOGRAPHICAL_TYPES?.EDIT
          ? geographicalState?.other?.geographicalAreaControlId
          : 0,
      geographicalAreaName: geographicalState.areaName,
      isActive: geographicalState.isActive,
      isDeleted: false,
      dsConfigId: streamObj?.dataStreamConfigId,
      zipCodeList: targetKeys,
    };
    try {
      const response =
        await geographicalAPi.saveOrUpdateGeographicalAreaControl(
          createRequest
        );
      if (response.status === 200) {
        if (response.data.status != 200) {
          ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
        } else {
          toast.success(t(`statusCodes.${response?.data?.code}`));
        }
        navigate(-1);
      } else {
        // toast.error(t("versionsForm.errorMessage.ruleSaveFailure"));
      }
    } catch (error) {
      // if (error.response.data.isDSConfigVersionPriorityAlready) {
      //   setPrioritySame(error.response.data.isDSConfigVersionPriorityAlready);
      // } else if (error.response.data.status != 200) {
      //   ToastUtils.serverError(t(`statusCodes.${error.response.data.code}`));
      // }
      toast.error(error.response?.data);
    }
  };

  const onSaveZipModal = () => {
    let takeMokeData = {
      key: zipCode?.toString(),
      title: zipCode,
    };
    setZipCodeData((prev) => [...prev, takeMokeData]);
    setShowAddZipCode(false);
    setZipCode("");
  };

  return (
    <>
      <CustomModal
        show={showAddZipCode}
        centered
        noHeader
        body={
          <div className="col-lg-12 col-md-12">
            <BasicTextInput
              type="number"
              label={t("geographical.zipCode")}
              isRequired={true}
              groupProps={{
                controlId: "zipCode",
              }}
              inputProps={{
                placeholder: t("geographical.zipCode"),
              }}
              name="zipCode"
              value={zipCode}
              onChange={handleChange}
              formLabelClass={{
                className: "mt-3",
              }}
            />

            {error && (
              <p
                style={{ height: "20px", fontSize: "13px" }}
                className="text-danger mt-1"
              >
                {error}
              </p>
            )}
          </div>
        }
        okTitle={t("CommonKeys.saveButtonTitle")}
        cancelTitle={t("CommonKeys.cancelButtonTitle")}
        centerFooter
        submitDisabled={!zipCode}
        onOk={() => {
          onSaveZipModal();
        }}
        onCancel={() => {
          setShowAddZipCode(false);
        }}
      />

      <CustomModal
        show={showSheetSelectionModal}
        centered
        // noHeader
        title={t("geographical.form.sheetListTitle")}
        body={
          <div className="col-lg-12 col-md-12">
            {(sheetNames ?? []).map((e, index) => {
              return (
                <Form.Check
                  key={`sheet-${index}`}
                  inline
                  type="radio"
                  name="sheetList"
                  id={`sheet-${index}`}
                  checked={index === selectedSheet}
                  label={e}
                  onChange={() => {
                    setSelectedSheet(index);
                  }}
                />
              );
            })}
          </div>
        }
        okTitle={t("CommonKeys.saveButtonTitle")}
        cancelTitle={t("CommonKeys.cancelButtonTitle")}
        centerFooter
        onOk={() => {
          loadZipCode(selectedSheet);
        }}
        onCancel={() => {
          setShowSheetSelectionModal(false);
        }}
      />

      <AiccBreadCrumb
        homeLink={getPathPrefix("dashboard")}
        title={streamObj?.customDataStreamName}
        items={t("geographical.geographicalAddArea.breadCrumb.items", {
          returnObjects: true,
        })}
      />
      <ContentCard
        headerImgSrc="/assets/images/globe.svg"
        title={`${breadCrumbTitle} - ${t("geographical.geographicalControl")}`}
        headerContent={
          <div className="col-12 col-md-8">
            <div className="d-block d-md-flex justify-content-end">
              <input
                ref={inputRef}
                onChange={handleDisplayFileDetails}
                className="d-none"
                type="file"
                accept=".xlsx, .xls, .csv"
              />
            </div>
          </div>
        }
        bodyContent={
          <>
            <div className="form">
              <div className="row">
                <div className="col-lg-6 col-md-6">
                  <BasicTextInput
                    label={t("geographical.form.areaName.label")}
                    isRequired={true}
                    groupProps={{
                      controlId: "rule-name",
                    }}
                    inputProps={{
                      placeholder: t("geographical.form.areaName.placeholder"),
                    }}
                    name="areaName"
                    value={geographicalState?.areaName}
                    onChange={(e) => {
                      updateErrorForInputs(
                        e.target.value,
                        "areaName",
                        "Please enter a area name"
                      );
                      setGeographicalState((prev) => ({
                        ...prev,
                        areaName: e.target.value,
                      }));
                    }}
                    onBlur={(e) => {
                      updateErrorForInputs(
                        e.target.value,
                        "areaName",
                        "Please enter a area name"
                      );
                    }}
                    disabled={isViewOnly}
                  />
                  {errors.areaName && (
                    <span
                      style={{ height: "20px", fontSize: "13px" }}
                      className="text-danger mt-1"
                    >
                      {errors.areaName}
                    </span>
                  )}
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="switch">
                    <label className="form-label">
                      {t("geographical.form.status")}
                    </label>
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      disabled={isViewOnly}
                      checked={geographicalState.isActive}
                      onChange={(e) => {
                        setGeographicalState((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="main-transfer-list">
              <VirtualTransfer
                data={zipCodeData}
                targetKeys={targetKeys}
                sourceHeader={t("geographical.form.sourceZipCodes")}
                targetHeader={t("geographical.form.selectedZipCodes")}
                sourceHeaderActions={
                  <>
                    <OverlayTrigger
                      placement="left-end"
                      overlay={(props) => (
                        <Tooltip id="button-tooltip" {...props}>
                          {t("geographical.uploadFromExcel")}
                        </Tooltip>
                      )}
                    >
                      <PrimaryButton
                        disabled={isViewOnly}
                        icon="fa-solid fa-file-arrow-up"
                        className=""
                        onClick={handleUpload}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="auto"
                      overlay={(props) => (
                        <Tooltip id="button-tooltip" {...props}>
                          {t("geographical.addZipCode")}
                        </Tooltip>
                      )}
                    >
                      <PrimaryButton
                        disabled={isViewOnly}
                        icon="fa-solid fa-plus"
                        className=""
                        onClick={() => {
                          setShowAddZipCode(true);
                        }}
                      />
                    </OverlayTrigger>
                  </>
                }
                onTargetKeysChanged={onTargetKeysChanged}
                disabled={isViewOnly}
              />
            </div>
          </>
        }
        footerContent={
          <div className="d-flex gap-2 mb-3 mx-3 justify-content-end">
            <PrimaryButton
              title={t("CommonKeys.backButtonTitle")}
              icon="fa-solid fa-arrow-left"
              onClick={() => {
                navigate(-1);
              }}
            />
            {!(type === CRUD_GEOGRAPHICAL_TYPES.VIEW) && (
              <PrimaryButton
                title={t("CommonKeys.saveButtonTitle")}
                icon="fa-regular fa-floppy-disk"
                onClick={() => {
                  onSubmitHandler();
                }}
                disabled={buttonDisabled}
              />
            )}
          </div>
        }
      />
    </>
  );
};
export default AddGeographicalArea;
